import { format } from "date-fns";

export interface DeliveryNoteData {
  businessName: string;
  businessPhone: string | null;
  businessEmail: string | null;
  businessAddress: string | null;
  supplierName: string;
  supplierPhone: string | null;
  supplierEmail: string | null;
  poNumber: string | null;
  deliveryDate: string;
  vehicleReg: string | null;
  docNumber: string | null;
  branchName: string | null;
  lines: { description: string; sku?: string; quantity: number }[];
  receivedBy: string | null;
  preparedByName: string;
  deliveryNotes: string | null;
}

interface DeliveryNoteProps {
  data: DeliveryNoteData;
}

const cellStyle: React.CSSProperties = {
  border: "1px solid #000",
  padding: "8px 12px",
  fontSize: "12px",
  fontFamily: "system-ui, -apple-system, sans-serif",
};

const headerCellStyle: React.CSSProperties = {
  ...cellStyle,
  fontWeight: 600,
  backgroundColor: "#f5f5f5",
};

export function DeliveryNote({ data }: DeliveryNoteProps) {
  const displayDate = data.deliveryDate
    ? format(new Date(data.deliveryDate), "dd MMM yyyy")
    : "—";
  const docNo = data.docNumber || data.poNumber || "—";

  return (
    <div
      id="delivery-note-print"
      className="delivery-note-print"
      style={{
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: "12px",
        color: "#000",
        backgroundColor: "#fff",
        padding: "20mm",
        maxWidth: "210mm",
        margin: "0 auto",
      }}
    >
      <style>{`
        @media screen {
          #delivery-note-print {
            position: absolute !important;
            left: -9999px !important;
            top: 0 !important;
            visibility: hidden !important;
          }
        }
        @media print {
          @page {
            size: A4;
            margin: 20mm;
          }
          body {
            font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
            color: #000;
            background: #fff;
          }
          body > * { display: none !important; }
          #delivery-note-print {
            display: block !important;
            position: static !important;
            left: auto !important;
            visibility: visible !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>

      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <tbody>
          {/* Header row: Business info | Delivery note info */}
          <tr>
            <td style={{ ...cellStyle, width: "50%", verticalAlign: "top" }}>
              <div style={{ fontWeight: 700, fontSize: "14px", marginBottom: "4px" }}>
                {data.businessName || "—"}
              </div>
              {(data.businessAddress || data.businessPhone) && (
                <div style={{ marginBottom: "2px" }}>
                  {[data.businessAddress, data.businessPhone].filter(Boolean).join(" • ")}
                </div>
              )}
              {data.businessEmail && (
                <div>{data.businessEmail}</div>
              )}
            </td>
            <td style={{ ...cellStyle, width: "50%", verticalAlign: "top", textAlign: "right" }}>
              <div style={{ fontWeight: 700, fontSize: "16px", marginBottom: "8px" }}>DELIVERY NOTE</div>
              <div>Date: {displayDate}</div>
              <div>Doc No: {docNo}</div>
              <div>Vehicle Reg: {data.vehicleReg || "—"}</div>
              <div>Customer Vehicle Reg No:</div>
            </td>
          </tr>

          {/* Bill To | Ship To */}
          <tr>
            <td style={{ ...cellStyle, verticalAlign: "top" }}>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>BILL TO:</div>
              <div>{data.supplierName}</div>
              {data.supplierPhone && <div>{data.supplierPhone}</div>}
              {data.supplierEmail && <div>{data.supplierEmail}</div>}
            </td>
            <td style={{ ...cellStyle, verticalAlign: "top" }}>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>SHIP TO:</div>
              <div>{data.businessName || "—"}</div>
              {data.branchName && <div>{data.branchName}</div>}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Items table */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "16px" }}>
        <thead>
          <tr>
            <th style={{ ...headerCellStyle, width: "60%", textAlign: "left" }}>ITEM DESCRIPTION</th>
            <th style={{ ...headerCellStyle, width: "20%", textAlign: "center" }}>UNIT</th>
            <th style={{ ...headerCellStyle, width: "20%", textAlign: "right" }}>QUANTITY</th>
          </tr>
        </thead>
        <tbody>
          {data.lines.map((line, idx) => (
            <tr key={idx}>
              <td style={{ ...cellStyle, textAlign: "left" }}>
                {line.sku ? `${line.description} (${line.sku})` : line.description}
              </td>
              <td style={{ ...cellStyle, textAlign: "center" }}>Each</td>
              <td style={{ ...cellStyle, textAlign: "right" }}>{line.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Terms */}
      <div style={{ ...cellStyle, marginTop: "16px", minHeight: "48px" }}>
        <div style={{ fontWeight: 600, marginBottom: "4px" }}>Terms and Conditions:</div>
        <div>
          I agree that the above that have been displayed are correct with regards to item name,
          quantity and description.
        </div>
      </div>

      {/* Signatures */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "16px" }}>
        <tbody>
          <tr>
            <td style={{ ...cellStyle, width: "33%", verticalAlign: "top" }}>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>Prepared By:</div>
              <div>{data.preparedByName}</div>
              <div style={{ marginTop: "20px" }}>Signature: _____________</div>
              <div>Date: {displayDate}</div>
            </td>
            <td style={{ ...cellStyle, width: "33%", verticalAlign: "top" }}>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>Delivered By:</div>
              <div style={{ minHeight: "1.2em" }}>&nbsp;</div>
              <div style={{ marginTop: "20px" }}>Signature: _____________</div>
              <div>Date: _____________</div>
            </td>
            <td style={{ ...cellStyle, width: "34%", verticalAlign: "top" }}>
              <div style={{ fontWeight: 600, marginBottom: "4px" }}>Received By:</div>
              <div>{data.receivedBy || "—"}</div>
              <div style={{ marginTop: "20px" }}>Signature: _____________</div>
              <div>Date: _____________</div>
            </td>
          </tr>
        </tbody>
      </table>

      {data.deliveryNotes && (
        <div style={{ marginTop: "12px", fontSize: "11px", color: "#333" }}>
          Notes: {data.deliveryNotes}
        </div>
      )}
    </div>
  );
}
